import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Content, Title, Button, ProductItem, Timer, Text } from 'components';
import { Empty, Flex } from 'antd';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { encrypt } from 'utils/Crypto';
import dayjs from 'dayjs';
import calculateApplyPolicy from 'utils/productPolicyUtils';
import Calculate from 'utils/Calculate';

const TimeSale = ({ data = [], products = [], screenSize }) => {
  const [progress, setProgress] = useState(0);

  const navigate = useNavigate();

  /* ===== 탭 관련 상태 ===== */
  const [selected, setSelected] = useState(null);

  const newData = React.useMemo(() => {
    if (!data.length || !products.length) return [];

    const newArr = data.map((item) => ({
      ...item,
      products: item?.map_product_policy
        ?.map((sub) => {
          const findProduct = products.find(
            (product) => product?.key === sub?.product_id
          );

          if (!findProduct) return null;

          const gradeBenefit = findProduct.policies.find(
            (item) => item.policy_type === 'GRADE'
          ) ?? {
            discount_rate: 0,
            discount_type: 'R',
            reserve_rate: 0,
            reserve_type: 'R',
          };

          const { apply_policy_info, final_discount_price } =
            calculateApplyPolicy({ price: findProduct.price }, gradeBenefit, {
              discount_rate: item.discount_rate,
              discount_type: item.discount_type,
              max_discount_price: item.max_discount_price,
              policy_type: 'PROMOTION',
            });

          return {
            ...sub,
            ...findProduct,
            discount: Calculate.calculateDiscountRate(
              findProduct.price,
              findProduct.price - final_discount_price
            ),
            discount_type: 'R',
            finalPrice: findProduct.price - final_discount_price,
            policies: apply_policy_info,
          };
        })
        ?.filter((sub) => sub?.key),
    }));

    return newArr;
  }, [data, products]);

  const now = React.useMemo(() => {
    return dayjs().unix();
  }, []);

  React.useEffect(() => {
    if (!newData.length) return;

    setSelected(newData?.[0]);
  }, [newData]);

  /* ===== 탭 스크롤 그라데이션 효과 ===== */
  const tabScrollRef = React.useRef(null);
  const [leftOpacity, setLeftOpacity] = useState(0);
  const [rightOpacity, setRightOpacity] = useState(1);

  const checkScroll = () => {
    const el = tabScrollRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setLeftOpacity(scrollLeft > 0 ? 1 : 0);
      setRightOpacity(scrollLeft + clientWidth < scrollWidth ? 1 : 0);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = tabScrollRef.current;
    el?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  useEffect(() => {
    if (!selected) return;

    const totalProducts = selected?.products?.length ?? 0;
    const visibleSlides = screenSize >= 1000 ? 4 : screenSize >= 700 ? 3 : 2;

    const progress =
      totalProducts <= visibleSlides
        ? 100
        : (visibleSlides / totalProducts) * 100;

    setProgress(progress);
  }, [selected, screenSize]);

  const styles = {
    title: {
      fontSize: screenSize >= 768 ? 58 : screenSize >= 500 ? 30 : 26,
      fontWeight: 700,
      lineHeight: screenSize >= 768 ? '88px' : '150%',
      paddingRight: 10,
    },
    timer: {
      backgroundColor: '#000',
      color: '#fff',
      fontWeight: 'bold',
      fontSize: 20,
      padding: '4px 8px',
      borderRadius: 6,
      minWidth: 36,
      textAlign: 'center',
    },
  };

  return (
    <div>
      <Content
        maxWidth={1500}
        padding={0}
        style={{
          width: screenSize >= 768 ? 'calc(100% - 60px)' : 'calc(100% - 30px)',
          backgroundColor: 'var(--begie-secondary-color)',
          margin: '0 auto',
        }}
        className="time_sale main_swiper"
      >
        {/* 탭 영역 */}
        <div className="button_wrap" style={{ position: 'relative' }}>
          {/* 왼쪽 그라데이션 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 40,
              height: '100%',
              background:
                'linear-gradient(to right, var(--begie-secondary-color), transparent)',
              opacity: leftOpacity,
              pointerEvents: 'none',
              transition: 'opacity 0.3s',
              zIndex: 1,
            }}
          />
          {/* 오른쪽 그라데이션 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 40,
              height: '100%',
              background:
                'linear-gradient(to left, var(--begie-secondary-color), transparent)',
              opacity: rightOpacity,
              pointerEvents: 'none',
              transition: 'opacity 0.3s',
              zIndex: 1,
            }}
          />

          <div
            ref={tabScrollRef}
            style={{
              display: 'flex',
              overflowX: 'auto',
              gap: 12,
              paddingBottom: 4,
              scrollbarWidth: 'none',
            }}
          >
            {newData?.map((_item) => (
              <Button
                key={`time_sale_${_item?.promotion_id}`}
                onClick={() => setSelected(_item)}
                className={
                  selected?.promotion_id === _item?.promotion_id
                    ? ''
                    : 'unsellected'
                }
              >
                {_item?.promotion_title}
              </Button>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: screenSize >= 768 ? 50 : 30,
            rowGap: 15,
          }}
        >
          <Title
            style={{
              ...styles.title,
              flexWrap: 'wrap',
            }}
            // className="dela-gothic"
          >
            {selected?.promotion_title ?? '-'}
          </Title>
          {now < selected?.promotion_start_date ? (
            <Flex gap={screenSize >= 768 ? 8 : 4} align="center">
              <Timer
                key={`promotion-timer-${selected?.promotion_id}`}
                start={now}
                end={dayjs.unix(selected?.promotion_start_date)}
                format={
                  selected?.promotion_start_date - now > 86400 ? 'DH' : 'HMS'
                }
                style={{ ...styles.timer }}
              />

              <Text size={screenSize >= 768 ? 18 : 14} weight={600}>
                후 시작
              </Text>
            </Flex>
          ) : now > selected?.promotion_end_date ? (
            <Flex
              style={{
                ...styles.timer,
              }}
            >
              <Text
                size={screenSize >= 768 ? 20 : 14}
                weight={600}
                color={'#FFFFFF'}
              >
                종료
              </Text>
            </Flex>
          ) : (
            <Flex gap={screenSize >= 768 ? 8 : 4} align="center">
              <Timer
                key={`promotion-timer-${selected?.promotion_id}`}
                start={dayjs.unix(selected?.promotion_start_date)}
                end={dayjs.unix(selected?.promotion_end_date)}
                format={
                  selected?.promotion_end_date - now > 86400 ? 'DH' : 'HMS'
                }
                style={{ ...styles.timer }}
              />
              <Text size={screenSize >= 768 ? 18 : 14} weight={600}>
                후 종료
              </Text>
            </Flex>
          )}
        </div>

        {/* 상품 슬라이더 */}
        {!selected?.products?.length ? (
          <Empty description={'등록된 상품이 없습니다.'} />
        ) : (
          <React.Fragment>
            <Swiper
              slidesPerView={screenSize >= 1000 ? 4 : screenSize >= 700 ? 3 : 2}
              spaceBetween={screenSize >= 1000 ? 20 : 15}
              modules={[Navigation]}
              loop={false}
              navigation
              onSlideChange={(swiper) => {
                const totalSlides = swiper.slides.length;
                const visibleSlides = swiper.params.slidesPerView;
                const currentProgress =
                  (swiper.activeIndex + visibleSlides) / totalSlides;
                const maxProgress =
                  totalSlides <= visibleSlides
                    ? 100
                    : Math.max(0, currentProgress * 100);
                setProgress(Math.min(100, maxProgress));
              }}
              style={{
                minHeight:
                  screenSize >= 1600
                    ? 690
                    : screenSize >= 400
                    ? '40vw'
                    : '95vw',
              }}
            >
              {selected?.products?.map((item, index) => (
                <SwiperSlide key={`product_${index}`}>
                  <div
                    onClick={() =>
                      navigate(
                        `/products/${encodeURIComponent(encrypt(item.key))}`
                      )
                    }
                  >
                    <ProductItem
                      src={item?.src}
                      title={item?.title}
                      price={item?.price}
                      discount={item?.discount}
                      discountType={item?.discount_type}
                      discountPrice={item.finalPrice}
                      hovered={false}
                      brand={item?.brand}
                      titleStyle={{ marginTop: 10 }}
                      isSoldOut={item?.isSoldOut === 'Y'}
                      policies={item.policies}
                      icons={[
                        { label: 'TIME', color: 'rgb(202, 97, 98)' },
                        ...(item?.isNew === 'Y'
                          ? [{ label: 'NEW', color: 'rgb(56, 56, 56)' }]
                          : []),
                      ]}
                    />
                    <ProductItem.Review
                      review={{ score: item?.score, comment: item?.comment }}
                      style={{ marginTop: 16 }}
                    />
                    <ProductItem.Tags
                      tags={item?.tags.map((tag) => ({
                        id: tag.tag_id,
                        label: tag.label,
                      }))}
                      style={{ marginBottom: 4, flexWrap: 'wrap' }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* 스크롤이 생길 때만 프로그레스 바 렌더링 */}
            {selected.products.length >
              (screenSize >= 1000 ? 4 : screenSize >= 700 ? 3 : 2) && (
              <div
                style={{
                  width: '100%',
                  height: '7px',
                  backgroundColor: '#ddd',
                  borderRadius: 15,
                  marginTop: screenSize >= 1800 ? 0 : 20,
                }}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: 'var(--black-color)',
                    transition: 'width 0.3s ease-in-out',
                    borderRadius: 15,
                  }}
                />
              </div>
            )}
          </React.Fragment>
        )}

        {data.length === 0 && <Empty style={{ width: '100%' }} />}
      </Content>
    </div>
  );
};

export default TimeSale;
